// ===== Lok AI 前端服务层 v5.2.0 =====
// 完全自建，Ollama 驱动，支持 JWT 认证

// 生产环境使用 Cloudflare Tunnel 地址，开发环境使用代理
const API_BASE = import.meta.env.VITE_API_BASE || "";

// ===== JWT Token 管理 =====
export function getToken(): string | null {
  return localStorage.getItem("lokai_token");
}
export function setToken(token: string) {
  localStorage.setItem("lokai_token", token);
}
export function removeToken() {
  localStorage.removeItem("lokai_token");
}
export function getStoredUser(): { username: string; role: string } | null {
  const raw = localStorage.getItem("lokai_user");
  return raw ? JSON.parse(raw) : null;
}
export function setStoredUser(user: { username: string; role: string }) {
  localStorage.setItem("lokai_user", JSON.stringify(user));
}
export function clearStoredUser() {
  localStorage.removeItem("lokai_user");
}

// ===== 游客模式：对话计数 =====
const GUEST_CONV_COUNT_KEY = "lokai_guest_conv_count";
const GUEST_CONV_THRESHOLD = 20;

export function getGuestConversationCount(): number {
  return parseInt(localStorage.getItem(GUEST_CONV_COUNT_KEY) || "0", 10);
}
export function incrementGuestConversationCount(): number {
  const count = getGuestConversationCount() + 1;
  localStorage.setItem(GUEST_CONV_COUNT_KEY, String(count));
  return count;
}
export function resetGuestConversationCount() {
  localStorage.removeItem(GUEST_CONV_COUNT_KEY);
}
export { GUEST_CONV_THRESHOLD };

// ===== 带认证的 fetch 封装 =====
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (!headers["Content-Type"] && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const resp = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (resp.status === 401) {
    removeToken();
    clearStoredUser();
    window.location.href = "/";
    return resp;
  }
  return resp;
}

// ===== 类型定义 =====

export interface CharacterTraits {
  shyness: number;
  warmth: number;
  assertiveness: number;
  jealousy: number;
  humor: number;
  formality: number;
}

export interface Character {
  id: string;
  name: string;
  description: string;
  avatar: string;
  tags: string[];
  category: string;
  gender: string;
  personality: string;
  background: string;
  scenario: string;
  greeting: string;
  system_prompt: string;
  example_messages: Array<{ role: string; content: string }>;
  nsfw: boolean;
  creator: string;
  creator_username: string;
  downloads: number;
  conversations: number;
  rating: number;
  rating_count: number;
  featured: boolean;
  traits?: CharacterTraits;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

export interface Conversation {
  id: string;
  character_id: string;
  character_name: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

export interface ConversationSummary {
  id: string;
  character_id: string;
  character_name: string;
  character_avatar: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message: string;
}

export interface UserSettings {
  ollama_endpoint: string;
  ollama_model: string;
  temperature: number;
  top_p: number;
  context_size: number;
  max_tokens: number;
  repeat_penalty: number;
  stop_sequences: string;
  stream: boolean;
  theme: string;
  language: string;
}

export interface SystemStatus {
  success: boolean;
  version: string;
  mode: string;
  ollama_online: boolean;
  model: string;
  characters_count: number;
  conversations_count: number;
}

// ===== 通用请求 =====

async function apiRequest<T>(path: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string>),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    removeToken();
    clearStoredUser();
    window.location.href = "/";
  }
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.detail || `API error: ${res.status}`);
  }
  return data;
}

// ===== 系统状态 =====

export async function getStatus(): Promise<SystemStatus> {
  return apiRequest("/api/status").then(r => r as unknown as SystemStatus);
}

// ===== 角色库 =====

export async function getCharacters(params?: {
  category?: string;
  search?: string;
  tag?: string;
  sort?: string;
  nsfw?: boolean;
}): Promise<Character[]> {
  const query = new URLSearchParams();
  if (params?.category && params.category !== "all") query.set("category", params.category);
  if (params?.search) query.set("search", params.search);
  if (params?.tag) query.set("tag", params.tag);
  if (params?.sort) query.set("sort", params.sort);
  if (params?.nsfw) query.set("nsfw", "true");
  const qs = query.toString();
  const data = await apiRequest<{ success: boolean; data: Character[] }>(`/api/characters${qs ? `?${qs}` : ""}`);
  return data.data;
}

export async function getCharacter(id: string): Promise<Character> {
  const data = await apiRequest<{ success: boolean; data: Character }>(`/api/characters/${id}`);
  return data.data;
}

export async function createCharacter(char: Partial<Character> & { avatar_seed?: string }): Promise<Character> {
  const data = await apiRequest<{ success: boolean; data: Character }>("/api/characters", {
    method: "POST",
    body: JSON.stringify(char),
  });
  return data.data;
}

export async function updateCharacter(id: string, char: Partial<Character> & { avatar_seed?: string }): Promise<Character> {
  const data = await apiRequest<{ success: boolean; data: Character }>(`/api/characters/${id}`, {
    method: "PUT",
    body: JSON.stringify(char),
  });
  return data.data;
}

export async function deleteCharacter(id: string): Promise<void> {
  await apiRequest(`/api/characters/${id}`, { method: "DELETE" });
}

export async function getTags(): Promise<string[]> {
  const data = await apiRequest<{ success: boolean; data: string[] }>("/api/tags");
  return data.data;
}

// ===== 对话 =====

export interface ChatResponse {
  success: boolean;
  data: {
    reply: string;
    message: Message;
    conversation_id: string;
  };
}

export async function sendMessage(params: {
  character_id: string;
  message: string;
  conversation_id?: string;
}): Promise<ChatResponse["data"]> {
  const data = await apiRequest<ChatResponse>("/api/chat", {
    method: "POST",
    body: JSON.stringify(params),
  });
  return data.data;
}

/** 流式对话 — 返回 ReadableStream，通过 SSE 读取 */
export async function sendMessageStream(params: {
  character_id: string;
  message: string;
  conversation_id?: string;
}): Promise<ReadableStream<Uint8Array>> {
  const url = `${API_BASE}/api/chat/stream`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok || !res.body) throw new Error("Stream request failed");
  return res.body;
}

/** 解析 SSE 流，调用 onContent 逐字输出，最后返回完整结果 */
export async function parseSSEStream(
  stream: ReadableStream<Uint8Array>,
  onContent: (chunk: string) => void,
  onStatus?: (status: string) => void,
): Promise<{ reply: string; conversation_id: string; message?: Message }> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let fullReply = "";
  let convId = "";
  let finalMessage: Message | undefined;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const text = decoder.decode(value, { stream: true });
    const lines = text.split("\n");
    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const json = JSON.parse(line.slice(6));
          if (json.status && onStatus) {
            onStatus(json.status);
          }
          if (json.content) {
            fullReply += json.content;
            onContent(json.content);
          }
          if (json.error) {
            throw new Error(json.error);
          }
          if (json.done) {
            convId = json.conversation_id || convId;
            finalMessage = json.message;
          }
        } catch (e) {
          if (e instanceof Error && e.message !== "Unexpected end of JSON input") throw e;
        }
      }
    }
  }

  return { reply: fullReply, conversation_id: convId, message: finalMessage };
}

// ===== 对话历史 =====

export async function getConversations(): Promise<ConversationSummary[]> {
  const data = await apiRequest<{ success: boolean; data: ConversationSummary[] }>("/api/conversations");
  return data.data;
}

export async function getConversation(id: string): Promise<Conversation> {
  const data = await apiRequest<{ success: boolean; data: Conversation }>(`/api/conversations/${id}`);
  return data.data;
}

export async function deleteConversation(id: string): Promise<void> {
  await apiRequest(`/api/conversations/${id}`, { method: "DELETE" });
}

/** 获取与某角色最近的对话（自动加载上下文） */
export async function getLatestConversation(character_id: string): Promise<Conversation | null> {
  const data = await apiRequest<{ success: boolean; data: Conversation | null }>(
    `/api/characters/${character_id}/latest-conversation`
  );
  return data.data;
}

// ===== 收藏 =====

export async function getFavorites(): Promise<Character[]> {
  const data = await apiRequest<{ success: boolean; data: Character[] }>("/api/favorites");
  return data.data;
}

export async function toggleFavorite(characterId: string): Promise<{ action: string }> {
  const data = await apiRequest<{ success: boolean; action: string }>(`/api/favorites/${characterId}`, {
    method: "POST",
  });
  return { action: data.action };
}

// ===== 用户设置 =====

export async function getSettings(): Promise<UserSettings> {
  const data = await apiRequest<{ success: boolean; data: UserSettings }>("/api/settings");
  return data.data;
}

export async function updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
  const data = await apiRequest<{ success: boolean; data: UserSettings }>("/api/settings", {
    method: "POST",
    body: JSON.stringify(settings),
  });
  return data.data;
}

// ===== Ollama 管理 =====

export async function getOllamaStatus(): Promise<{
  success: boolean;
  online: boolean;
  endpoint: string;
  model: string;
  available_models: string[];
}> {
  return apiRequest("/api/ollama/status");
}

export async function getOllamaModels(): Promise<Array<{ name: string; size: number; modified_at: string }>> {
  const data = await apiRequest<{ success: boolean; models: Array<{ name: string; size: number; modified_at: string }> }>("/api/ollama/models");
  return data.models;
}

export async function pullModel(modelName: string): Promise<void> {
  await apiRequest(`/api/ollama/pull?model_name=${encodeURIComponent(modelName)}`, {
    method: "POST",
  });
}

export async function redeployModel(modelName?: string): Promise<{
  success: boolean;
  message: string;
  model: string;
  steps: Array<{ step: number; name: string; status: string; output?: string; error?: string }>;
}> {
  return await apiRequest("/api/ollama/redeploy", { 
    method: "POST", 
    body: modelName ? JSON.stringify({ model: modelName }) : undefined 
  });
}

// ===== 分类常量 =====

export const CHARACTER_CATEGORIES = [
  { id: "all", label: "全部", icon: "🌟" },
  { id: "featured", label: "精选", icon: "⭐" },
  { id: "favorites", label: "收藏", icon: "❤️" },
  { id: "romance", label: "浪漫", icon: "💕" },
  { id: "fantasy", label: "奇幻", icon: "🔮" },
  { id: "adventure", label: "冒险", icon: "⚔️" },
  { id: "scifi", label: "科幻", icon: "🚀" },
  { id: "anime", label: "动漫", icon: "✨" },
  { id: "roleplay", label: "角色扮演", icon: "🎭" },
  { id: "horror", label: "恐怖", icon: "👻" },
  { id: "comedy", label: "搞笑", icon: "😂" },
];

export const SORT_OPTIONS = [
  { id: "featured", label: "精选推荐" },
  { id: "popular", label: "最受欢迎" },
  { id: "rating", label: "最高评分" },
  { id: "newest", label: "最新发布" },
  { id: "downloads", label: "最多下载" },
];

// ===== 多模态 API (Phase 3) =====

export interface TTSVoice {
  id: string;
  name: string;
  label: string;
  gender: string;
}

export async function getTTSVoices(): Promise<TTSVoice[]> {
  const data = await apiRequest<{ success: boolean; data: TTSVoice[] }>("/api/tts/voices");
  return data.data;
}

export interface STTModel {
  id: string;
  name: string;
  size: string;
  speed: string;
  accuracy: string;
}

export async function getSTTModels(): Promise<STTModel[]> {
  const data = await apiRequest<{ success: boolean; data: STTModel[] }>("/api/stt/models");
  return data.data;
}

export interface ImageBackend {
  id: string;
  name: string;
  description: string;
  available: boolean;
}

export async function getImageBackends(): Promise<ImageBackend[]> {
  const data = await apiRequest<{ success: boolean; data: ImageBackend[] }>("/api/image/backends");
  return data.data;
}

export async function getAvatarStyles(): Promise<string[]> {
  const data = await apiRequest<{ success: boolean; data: string[] }>("/api/image/avatar-styles");
  return data.data;
}

// ===== 多人聊天 API (Phase 4) =====

export interface ChatRoom {
  id: string;
  name: string;
  character_ids: string[];
  characters: Array<{ id: string; name: string; avatar: string }>;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface RoomMessage {
  id: string;
  role: "user" | "assistant";
  character_id?: string;
  content: string;
  charname?: string;
  timestamp: string;
}

export interface RoomDetail {
  id: string;
  name: string;
  character_ids: string[];
  messages: RoomMessage[];
  created_at: string;
  updated_at: string;
}

export async function getChatRooms(): Promise<ChatRoom[]> {
  const data = await apiRequest<{ success: boolean; data: ChatRoom[] }>("/api/rooms");
  return data.data;
}

export async function createChatRoom(name: string, character_ids: string[]): Promise<ChatRoom> {
  const data = await apiRequest<{ success: boolean; data: ChatRoom }>("/api/rooms", {
    method: "POST",
    body: JSON.stringify({ name, character_ids }),
  });
  return data.data;
}

export async function getChatRoom(room_id: string): Promise<RoomDetail> {
  const data = await apiRequest<{ success: boolean; data: RoomDetail }>(`/api/rooms/${room_id}`);
  return data.data;
}

export async function deleteChatRoom(room_id: string): Promise<void> {
  await apiRequest(`/api/rooms/${room_id}`, { method: "DELETE" });
}

export interface RoomChatReply {
  character_id: string;
  character_name: string;
  content?: string;
  error?: string;
  message?: RoomMessage;
}

export async function sendRoomMessage(params: {
  room_id: string;
  message: string;
  character_id?: string;
}): Promise<{ room_id: string; replies: RoomChatReply[] }> {
  const data = await apiRequest<{ success: boolean; data: { room_id: string; replies: RoomChatReply[] } }>(
    "/api/rooms/chat",
    { method: "POST", body: JSON.stringify(params) }
  );
  return data.data;
}

export async function sendRoomMessageStream(params: {
  room_id: string;
  message: string;
  character_id?: string;
}): Promise<ReadableStream<Uint8Array>> {
  const url = `${API_BASE}/api/rooms/chat/stream`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok || !res.body) throw new Error("Stream request failed");
  return res.body;
}

// ===== 角色导入导出 API (Phase 4) =====

// ===== 头像上传 API =====

export async function uploadAvatarAPI(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}/api/upload-avatar`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${getToken()}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "上传失败");
  return data.data;
}

export interface CharacterExport {
  version: string;
  exported_at: string;
  character: Character;
  memories: {
    summary: Record<string, unknown>;
    facts: Array<Record<string, unknown>>;
  };
}

export async function exportCharacter(character_id: string): Promise<CharacterExport> {
  const data = await apiRequest<{ success: boolean; data: CharacterExport }>(
    `/api/characters/${character_id}/export`
  );
  return data.data;
}

export async function importCharacter(charData: Partial<Character> & { memories?: string[] }): Promise<Character> {
  const data = await apiRequest<{ success: boolean; data: Character }>("/api/characters/import", {
    method: "POST",
    body: JSON.stringify(charData),
  });
  return data.data;
}

export async function importCharacterFile(file: File): Promise<Character> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}/api/characters/import/file`, {
    method: "POST",
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Import failed");
  return data.data;
}

// ===== 认证 API =====

export async function loginAPI(username: string, password: string) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "登录失败");
  return data as { success: boolean; token: string; username: string; role: string };
}

export async function registerAPI(username: string, password: string) {
  const res = await fetch(`${API_BASE}/api/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "注册失败");
  return data as { success: boolean; token: string; username: string; role: string };
}

export async function getMeAPI() {
  return apiRequest<{ success: boolean; data: { username: string; role: string; created_at: string; last_login: string } }>("/api/auth/me");
}

// ===== 管理后台 API =====

export async function adminLoginAPI(password: string) {
  const res = await fetch(`${API_BASE}/api/admin/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "管理员登录失败");
  return data as { success: boolean; token: string; username: string; role: string };
}

export async function adminGetUsersAPI() {
  return apiRequest<{ success: boolean; data: Array<{
    username: string; role: string; created_at: string; last_login: string;
    disabled: boolean; chat_count: number;
  }> }>("/api/admin/users");
}

export async function adminEditUserAPI(username: string, data: { password?: string; role?: string; disabled?: boolean }) {
  return apiRequest<{ success: boolean; message: string }>(`/api/admin/users/${username}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function adminDeleteUserAPI(username: string) {
  return apiRequest<{ success: boolean; message: string }>(`/api/admin/users/${username}`, {
    method: "DELETE",
  });
}

export async function adminGetUserLogsAPI(username: string) {
  return apiRequest<{ success: boolean; data: Array<{ action: string; detail: string; timestamp: string }> }>(`/api/admin/users/${username}/logs`);
}

export async function adminGetStatsAPI() {
  const res = await apiRequest("/api/admin/stats") as any;
  return res;
}
